import { useEffect } from "react";

type InstallChoice = { readonly outcome: "accepted" | "dismissed" };

interface BeforeInstallPromptEvent extends Event {
  readonly prompt: () => Promise<void>;
  readonly userChoice: Promise<InstallChoice>;
}

const INSTALL_OFFERED_KEY = "chams-pay-install-offered";

function installedAsApp(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { readonly standalone?: boolean }).standalone === true
  );
}

function offeredThisVisit(): boolean {
  try {
    return window.sessionStorage.getItem(INSTALL_OFFERED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberOffer(): void {
  installOffered = true;
  try {
    window.sessionStorage.setItem(INSTALL_OFFERED_KEY, "1");
  } catch {
    // The in-memory guard still prevents repeat prompts when storage is unavailable.
  }
}

let deferredInstall: BeforeInstallPromptEvent | null = null;
let prompting = false;
let installOffered = offeredThisVisit();

window.addEventListener("beforeinstallprompt", (event) => {
  if (installedAsApp()) return;
  event.preventDefault();
  if (installOffered) return;
  deferredInstall = event as BeforeInstallPromptEvent;
});
window.addEventListener("appinstalled", () => {
  rememberOffer();
  deferredInstall = null;
});

/**
 * Uses only the browser-owned PWA installation prompt. Browsers require
 * `prompt()` during a user activation, so the first eligible interaction
 * invokes the deferred native prompt without an intermediate Chams.Pay dialog.
 */
export function MobileInstallPrompt() {
  useEffect(() => {
    if (installedAsApp()) return;

    const offerNativeInstall = () => {
      const event = deferredInstall;
      if (!event || prompting) return;
      deferredInstall = null;
      prompting = true;
      rememberOffer();
      void event
        .prompt()
        .then(() => event.userChoice)
        .finally(() => {
          prompting = false;
        });
    };

    window.addEventListener("pointerup", offerNativeInstall, true);
    window.addEventListener("keydown", offerNativeInstall, true);
    return () => {
      window.removeEventListener("pointerup", offerNativeInstall, true);
      window.removeEventListener("keydown", offerNativeInstall, true);
    };
  }, []);

  return null;
}
